/**
 * The words this documentation uses, and the ones it does not.
 *
 * Every product has three names for the same thing — workspace, organisation,
 * account — and documentation written by more than one person over more than
 * a month will use all three. A reader who searches for the word they were
 * shown in the interface and finds a page using a different one does not think
 * "synonym", they think "this is about something else".
 *
 * CONTENT, not settings, for exactly the reasons `search.yaml` is: it belongs
 * to the same people, changes for the same reasons, is reviewed in the same
 * draft, and leaves with the customer if they do. A database row would be
 * invisible to review and lost on export.
 *
 * A WARNING, never a refusal. Language is a judgement, and a linter that
 * blocks a publish over one is a linter people route around — every real
 * vocabulary has a page where the wrong word is the right word, quoting an
 * error message or naming somebody else's product.
 */
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { keepMapComments } from './index.js';
export const EMPTY_GLOSSARY = { terms: [] };
/**
 * What a new glossary says before anybody has written a term.
 *
 * Comments and nothing else, so it parses to an empty glossary and warns about
 * nothing until somebody means it. The example is the one every product has —
 * three names for the place work lives — because a starter file that explains
 * the format with `foo` teaches the format and not the point.
 */
export const GLOSSARY_STARTER = `# The words this documentation uses, and the ones it does not.
#
# A reader who searches for the word your interface showed them and lands on a
# page using a different one does not think "synonym". They think the page is
# about something else.
#
# Each term is a word to use and the words to replace. It is a WARNING, never a
# refusal: quoting an error message or naming somebody else's field is exactly
# when the other spelling is correct, and code, links and frontmatter are never
# checked at all.
#
# terms:
#   - use: workspace
#     not: [organisation, org, account]
#     because: Billing is per workspace, and an account is a person.
#   - use: sign in
#     not: [log in, login]
`;
/**
 * The longest phrase worth matching, in words.
 *
 * A "term" here is a word or a short phrase — "sign in", "single sign-on". A
 * sentence in `not` is somebody trying to write a style guide in a field meant
 * for vocabulary, and matching one would produce advice nobody can act on.
 */
const MOST_WORDS = 5;
export function parseGlossary(yaml) {
    const raw = yaml.trim() === '' ? {} : parseYaml(yaml);
    if (raw === null || raw === undefined)
        return { terms: [] };
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error('glossary.yaml must be a mapping with one key: terms');
    }
    const map = raw;
    for (const key of Object.keys(map)) {
        if (key !== 'terms')
            throw new Error(`"${key}" is not a glossary setting — the only key is terms`);
    }
    const list = map['terms'];
    if (list === undefined || list === null)
        return { terms: [] };
    if (!Array.isArray(list)) {
        throw new Error('terms must be a list, like `- use: workspace` / `  not: [organisation]`');
    }
    const seen = new Set();
    const terms = list.map((entry, index) => {
        const at = `term ${index + 1}`;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
            throw new Error(`${at} must be a mapping with "use" and "not"`);
        }
        const row = entry;
        for (const key of Object.keys(row)) {
            if (!['use', 'not', 'because'].includes(key)) {
                throw new Error(`"${key}" is not part of a term — use, not, because`);
            }
        }
        const use = String(row['use'] ?? '').trim();
        if (use === '')
            throw new Error(`${at} has no word to prefer`);
        if (words(use) > MOST_WORDS)
            throw new Error(`"${use}" is a sentence, not a term`);
        const notRaw = row['not'];
        const not = (Array.isArray(notRaw) ? notRaw : notRaw === undefined || notRaw === null ? [] : [notRaw])
            .map((word) => String(word).trim().toLowerCase())
            .filter((word) => word !== '');
        /**
         * A term with nothing to replace is refused rather than ignored.
         *
         * It is always a half-finished edit — somebody added the preferred word
         * and meant to come back — and silence about it is how it stays that way.
         */
        if (not.length === 0)
            throw new Error(`"${use}" lists no words to replace, so it does nothing`);
        for (const word of not) {
            if (words(word) > MOST_WORDS)
                throw new Error(`"${word}" is a sentence, not a term`);
            if (word === use.toLowerCase()) {
                throw new Error(`"${use}" is listed as its own discouraged word`);
            }
            // The same word preferred in one term and discouraged in another leaves
            // a writer with no move that satisfies both.
            if (seen.has(word))
                throw new Error(`"${word}" appears in more than one term`);
            seen.add(word);
        }
        const because = row['because'] === undefined ? undefined : String(row['because']).trim();
        return { use, not, ...(because ? { because } : {}) };
    });
    /**
     * A word cannot be both the answer and the mistake.
     *
     * Checked across the whole file rather than per term, because the two halves
     * can be written in either order — and a glossary that prefers "workspace"
     * in one entry and discourages it in another leaves a writer with no move
     * that satisfies both, which reads as the tool being broken.
     */
    const preferred = new Set(terms.map((term) => term.use.toLowerCase()));
    for (const term of terms) {
        for (const word of term.not) {
            if (preferred.has(word)) {
                throw new Error(`"${word}" is preferred by one term and discouraged by another`);
            }
        }
    }
    return { terms };
}
const words = (value) => value.trim().split(/\s+/).length;
/** Write it back, keeping the notes somebody left in it. */
export function serializeGlossary(glossary, previous) {
    const text = stringifyYaml({
        terms: glossary.terms.map((term) => ({
            use: term.use,
            not: term.not,
            ...(term.because ? { because: term.because } : {}),
        })),
    });
    return previous === undefined ? text : keepMapComments(text, previous);
}
//# sourceMappingURL=glossary.js.map